import { useState } from "react";
import { Button, DenkerLogo } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import {
  ComposerRedesign,
  type ComposerDirection,
} from "@/workshop/components/panels/composer-redesign";
import "./composer-redesign.css";

const DIRECTIONS: {
  id: ComposerDirection;
  title: string;
  description: string;
}[] = [
  {
    id: "inline",
    title: "01 · One line",
    description:
      "One row to start. Longer drafts grow upward across the full width.",
  },
  {
    id: "integrated",
    title: "02 · Quiet footer",
    description: "One surface. Full-width writing, controls that stay put.",
  },
  {
    id: "rail",
    title: "03 · Floating rail",
    description:
      "The writing surface grows above a separate, lightweight control row.",
  },
];
const LONG_DRAFT =
  "Help me shape the launch plan for Denker.\n\nStart with the people we are building for, then outline the three most important things they should be able to do on their first day.\n\nKeep the language clear and specific. I'd like something we can discuss together before we turn it into tasks.";

export function ComposerRedesignPage() {
  const [direction, setDirection] = useState<ComposerDirection>("inline");
  const [draft, setDraft] = useState("");
  const [reply, setReply] = useState("");
  const [narrow, setNarrow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [lastSent, setLastSent] = useState("");
  return (
    <div
      className="dark composer-lab min-h-screen bg-canvas p-8 text-primary"
      data-testid="composer-lab"
    >
      <header className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center gap-2 text-xs text-muted">
          <DenkerLogo color="white" height={18} /> DENKER / WORKSHOP / COMPOSER
        </div>
        <h1 className="text-3xl font-medium tracking-tight">Room to think.</h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-secondary">
          A calmer input for a quick thought or a longer brief. Try a direction,
          type a few lines, and open the model menu.
        </p>
        <div className="mt-7 grid gap-3 md:grid-cols-3">
          {DIRECTIONS.map((item) => (
            <Button
              key={item.id}
              variant="secondary"
              aria-pressed={direction === item.id}
              onClick={() => setDirection(item.id)}
              className="composer-control h-auto flex-col items-start whitespace-normal rounded-2xl p-4 text-left shadow-none"
              data-testid={`composer-direction-${item.id}`}
            >
              <span className="text-sm text-primary">{item.title}</span>
              <span className="mt-2 text-xs font-normal leading-relaxed text-muted">
                {item.description}
              </span>
            </Button>
          ))}
        </div>
        <div className="my-6 flex flex-wrap items-center gap-2">
          <span className="mr-2 text-xs text-muted">Try it</span>
          <Button
            variant="secondary"
            size="sm"
            className="composer-control"
            onClick={() => {
              setDraft(LONG_DRAFT);
              setReply(LONG_DRAFT);
            }}
            data-testid="composer-long-draft"
          >
            Long draft
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="composer-control"
            onClick={() => {
              setDraft("");
              setReply("");
            }}
            data-testid="composer-clear-draft"
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="composer-control"
            aria-pressed={narrow}
            onClick={() => setNarrow(!narrow)}
            data-testid="composer-narrow"
          >
            Narrow width
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="composer-control"
            aria-pressed={busy}
            onClick={() => setBusy(!busy)}
            data-testid="composer-busy"
          >
            Responding
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="composer-control"
            aria-pressed={disabled}
            onClick={() => setDisabled(!disabled)}
            data-testid="composer-disabled"
          >
            Unavailable
          </Button>
        </div>
      </header>
      <main className="mx-auto grid max-w-6xl items-start gap-8 lg:grid-cols-2">
        <section>
          <div className="mb-3 flex items-center justify-between text-xs text-muted">
            <span>STANDALONE</span>
            <span>{narrow ? "360" : "440"} px</span>
          </div>
          <div className="flex min-h-[410px] flex-col justify-end rounded-3xl border border-glass-stroke-subtle bg-surface/50 p-5">
            <div
              className={cn(
                "mx-auto w-full",
                narrow ? "max-w-[360px]" : "max-w-[440px]",
              )}
            >
              {lastSent && (
                <p
                  className="mb-5 max-h-24 overflow-auto whitespace-pre-wrap break-words text-sm text-secondary"
                  role="status"
                >
                  Sent in workshop: {lastSent}
                </p>
              )}
              <ComposerRedesign
                direction={direction}
                value={draft}
                onChange={setDraft}
                onSend={() => {
                  setLastSent(draft);
                  setDraft("");
                }}
                busy={busy}
                disabled={disabled}
                onStop={() => setBusy(false)}
                testId="standalone-composer"
              />
            </div>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted">
            The bottom edge stays anchored as the draft grows upward.
          </p>
        </section>
        <section>
          <div className="mb-3 flex items-center justify-between text-xs text-muted">
            <span>IN CONVERSATION</span>
            <span>{narrow ? "360" : "440"} px</span>
          </div>
          <div
            className={cn(
              "mx-auto flex h-[410px] w-full flex-col rounded-3xl border border-glass-stroke bg-surface shadow-glass-sm",
              narrow ? "max-w-[360px]" : "max-w-[440px]",
            )}
          >
            <div className="flex items-center gap-2 border-b border-glass-stroke-subtle px-5 py-4 text-sm">
              <Icons.MessageCircle className="h-4 w-4 text-muted" /> Launch plan{" "}
              <span className="ml-auto text-xs text-muted">Denker</span>
            </div>
            <div
              className="min-h-0 flex-1 space-y-4 overflow-auto px-5 py-5 text-sm leading-relaxed"
              data-testid="composer-conversation-messages"
            >
              <p className="ml-10 rounded-2xl bg-glass-fill px-3 py-2 text-secondary">
                What should we focus on for launch?
              </p>
              <div>
                <p className="mb-2 text-xs text-muted">Denker</p>
                <p>
                  Make the first five minutes feel useful. Start with one clear
                  task, show the result, and give people an easy next step.
                </p>
              </div>
              {messages.map((message, index) => (
                <p
                  key={index}
                  className="ml-10 whitespace-pre-wrap break-words rounded-2xl bg-glass-fill px-3 py-2 text-secondary"
                >
                  {message}
                </p>
              ))}
              {busy && (
                <p role="status" className="text-xs text-muted">
                  Denker is thinking…
                </p>
              )}
            </div>
            <div className="shrink-0 p-3">
              <ComposerRedesign
                direction={direction}
                value={reply}
                onChange={setReply}
                onSend={() => {
                  setMessages([...messages, reply]);
                  setReply("");
                }}
                busy={busy}
                disabled={disabled}
                onStop={() => setBusy(false)}
                testId="conversation-composer"
              />
            </div>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted">
            The same composer, with room for the conversation above it.
          </p>
        </section>
      </main>
      <p className="mx-auto mt-7 max-w-6xl text-xs text-muted">
        Enter to send · Shift + Enter for a new line · Demo data, local
        interactions only
      </p>
    </div>
  );
}
